

import { useState } from "react";
import { Search } from "lucide-react";
import DateRangePicker from "../../../components/DateRangePicker";

interface EventFiltersProps {
  search: string;
  setSearch: (value: string) => void;
  dateRange: { from: string; to: string };
  setDateRange: (range: { from: string; to: string } | ((prev: { from: string; to: string }) => { from: string; to: string })) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  eventTypeFilter: string;
  setEventTypeFilter: (value: string) => void;
  venueFilter: string;
  setVenueFilter: (value: string) => void;
  eventTypes: Array<{ id: number; name: string }>;
  venues: Array<{ id: number; name: string }>;
  onApply: () => void;
}

export function EventFilters({
  search,
  setSearch,
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  eventTypeFilter,
  setEventTypeFilter,
  venueFilter,
  setVenueFilter,
  eventTypes,
  venues,
  onApply,
}: EventFiltersProps) {
  const [showFilters, setShowFilters] = useState(true);
  const hasActiveFilters = statusFilter || eventTypeFilter || venueFilter;

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4">
      {/* Mobile filter toggle */}
      <div className="md:hidden flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-stone-500" />
          <span className="text-sm font-medium text-stone-700">
            {hasActiveFilters ? 'Filters active' : 'Filters'}
          </span>
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="text-sm text-blue-600 font-medium"
        >
          {showFilters ? 'Hide' : 'Show'}
        </button>
      </div>

      <form 
        onSubmit={(e) => {
          e.preventDefault();
          onApply();
        }} 
        className={`flex flex-col lg:flex-row gap-3 ${!showFilters ? 'hidden md:flex' : ''}`}
      >
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search events or clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
            />
          </div>
        </div>

        <DateRangePicker
          fromDate={dateRange.from}
          toDate={dateRange.to}
          onFromChange={(date) => setDateRange((prev: { from: string; to: string }) => ({ ...prev, from: date }))}
          onToChange={(date) => setDateRange((prev: { from: string; to: string }) => ({ ...prev, to: date }))}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation bg-white min-h-[42px]"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          className="px-3 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation bg-white min-h-[42px]"
        >
          <option value="">All Event Types</option>
          {eventTypes.map((type) => (
            <option key={type.id} value={type.name}>{type.name}</option>
          ))}
        </select>

        {venues.length > 0 && (
          <select
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation bg-white min-h-[42px]"
          >
            <option value="">All Venues</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>{venue.name}</option>
            ))}
          </select>
        )}

        <button
          type="submit"
          className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          Apply Filters
        </button>
      </form>
    </div>
  );
}
